"use client";
import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { useAgent } from "../../../utils/agent/AgentProvider.js";
import { ModuleSettingsStore } from "../../../utils/contentstack/moduleSettingsStore.js";
import { useReducer, useState, useEffect, useMemo } from "react";
import { useTracking } from "react-tracking";
import { Modal } from "@nxt-costco-com/forge-components";
import { Analytics } from "../../Analytics/index.js";
import { SAVE_TO_LIST } from "../../Analytics/constants.js";
import { SaveToListDrawer } from "./SaveToList/index.js";
import { useAuth } from "../../../hooks/auth/useAuth/index.js";
import { AddToListButton } from "./AddToListButton/index.js";
import { AddToListModalBody } from "./AddToListModalBody/index.js";
import { useAddToListContext } from "./AddToListProvider/useAddToListContext.js";
import { AddToListReducer } from "./AddToListReducer.js";
import { addToListInitialState, addToListActions, defaultModalFooterButtonsLabels, COMPONENT_NAME } from "./constants.js";
import { handleModalButtonsText, handleModalFooterActions } from "./lib.js";
const AddToList = ({
  product,
  variant = "icon",
  uniqueId,
  sx,
  ariaDescribedBy,
  buttonSize = "medium",
  productAttributes = []
}) => {
  const { trackEvent } = useTracking();
  const {
    isUserSignedIn,
    isLoading: isAuthUtilsLoading,
    getToken,
    signIn
  } = useAuth();
  const { agent } = useAgent();
  const [state, dispatch] = useReducer(AddToListReducer, {
    ...addToListInitialState,
    productAttributes
  });
  const { contentStackValues, enableModernApis } = useAddToListContext();
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(
    false
  );
  useEffect(() => {
    if (!isAuthUtilsLoading) {
      isUserSignedIn().then((result) => {
        setIsUserLoggedIn(result);
      });
    }
  }, [isAuthUtilsLoading, isUserSignedIn]);
  const msStore = useMemo(() => {
    if (!contentStackValues) {
      return;
    }
    return new ModuleSettingsStore({
      composable_window: contentStackValues == null ? void 0 : contentStackValues.composable_window,
      key_value: contentStackValues == null ? void 0 : contentStackValues.key_value,
      properties: contentStackValues == null ? void 0 : contentStackValues.properties
    });
  }, [contentStackValues]);
  if (!contentStackValues || !msStore) {
    console.error("Missing Save to List Module Settings Data");
    return null;
  }
  const {
    addToListModalMode,
    isLoading,
    selectedListId,
    addToListModalOpen
  } = state;
  const modalFooterButtonsLabels = {
    addItem: msStore.getKeyValueItem("additembuttonlabel"),
    cancel: msStore.getKeyValueItem("cancelbuttonlabel"),
    continueShopping: msStore.getKeyValueItem("continuebuttonlabel"),
    createList: msStore.getKeyValueItem("createlistbuttonlabel"),
    viewList: msStore.getKeyValueItem("viewlistbuttonlabel")
  };
  const footerButtonsText = handleModalButtonsText(addToListModalMode, {
    ...defaultModalFooterButtonsLabels,
    ...modalFooterButtonsLabels
  });
  const addButtonLabel = msStore.getKeyValueItem("addtolistbuttonlabel");
  const addToListModalTitle = msStore.getKeyValueItem("addtolisttitle");
  const saveToListDrawerTitle = msStore.getKeyValueItem(
    "savetolistdrawertitle"
  );
  const saveToListButtonLabel = msStore.getKeyValueItem(
    "savetolistbuttonlabel"
  );
  const viewListUrl = msStore.getPropertiesItem("listPageURL");
  const onClickAddToListButton = (trackedEventTitle, isSavedToList) => {
    var _a;
    const pageType = ((_a = window == null ? void 0 : window.digitalData) == null ? void 0 : _a.pageName) ? window.digitalData.pageName : "";
    if (!isUserLoggedIn) {
      return signIn();
    }
    if (trackedEventTitle) {
      trackEvent({
        trackType: SAVE_TO_LIST,
        trackedData: `ui: ${pageType} | ${trackedEventTitle}`
      });
    }
    if (isSavedToList) {
      dispatch({
        payload: {
          addToListModalOpen: true,
          isLoading: true,
          wishListFetched: false
        },
        type: addToListActions.UPDATE_STATE
      });
    } else {
      dispatch({
        payload: { addToListModalOpen: true },
        type: addToListActions.CHANGE_MODAL_STATE
      });
    }
  };
  if (variant === "iconV2") {
    return /* @__PURE__ */ jsx(Analytics, { children: /* @__PURE__ */ jsx(
      SaveToListDrawer,
      {
        addToListModalOpen,
        dispatch,
        enableModernApis,
        locale: contentStackValues.locale,
        msStore,
        product,
        state,
        title: saveToListDrawerTitle,
        uniqueId,
        children: /* @__PURE__ */ jsx(
          AddToListButton,
          {
            handleClick: () => onClickAddToListButton(
              "Save to List Heart Icon",
              true
            ),
            ariaDescribedBy,
            buttonLabel: saveToListButtonLabel,
            buttonSize,
            sx,
            uniqueId,
            variant
          }
        )
      }
    ) });
  }
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      AddToListButton,
      {
        ariaDescribedBy,
        buttonLabel: addButtonLabel,
        buttonSize,
        handleClick: () => onClickAddToListButton(void 0),
        sx,
        uniqueId,
        variant
      }
    ),
    /* @__PURE__ */ jsx(
      Modal,
      {
        buttons: [
          {
            action: async () => {
              let authCookie;
              try {
                authCookie = await getToken();
              } catch {
                console.error("Token fetch failed");
              }
              if (authCookie) {
                handleModalFooterActions(
                  enableModernApis,
                  state,
                  dispatch,
                  msStore,
                  product,
                  authCookie,
                  contentStackValues.locale,
                  agent
                );
              }
            },
            text: footerButtonsText == null ? void 0 : footerButtonsText.first,
            ...addToListModalMode === addToListActions.UPDATED_TO_LIST && {
              href: viewListUrl.replace(
                "{listId}",
                selectedListId
              )
            },
            isLoading
          },
          {
            action: () => dispatch({
              payload: { addToListModalOpen: false },
              type: addToListActions.CHANGE_MODAL_STATE
            }),
            text: footerButtonsText == null ? void 0 : footerButtonsText.second
          }
        ],
        onClose: () => dispatch({
          payload: { addToListModalOpen: false },
          type: addToListActions.CHANGE_MODAL_STATE
        }),
        isOpen: addToListModalOpen,
        modalTitle: addToListModalTitle,
        uniqueId,
        children: /* @__PURE__ */ jsx(
          AddToListModalBody,
          {
            dispatch,
            enableModernApis,
            msStore,
            product,
            state
          }
        )
      }
    )
  ] });
};
AddToList.displayName = COMPONENT_NAME;
export {
  AddToList
};
